<template>
  <el-dialog
    :visible.sync="dialogVisible"
    width="1200px"
    :show-close="false"
    custom-class="asset-detail-dialog"
  >
    <div class="dialog-header">
      <div class="header-left">
        <div class="asset-icon">
          <img v-if="assetInfo.icon" :src="assetInfo.icon" class="icon-image" />
          <span v-else class="icon-text">{{
            assetInfo.assetType && assetInfo.assetType.charAt(0)
          }}</span>
        </div>
        <span class="asset-title">{{ displayCurrency }}</span>
      </div>
      <img
        class="close-img"
        alt="close-icon.png"
        @click="dialogVisible = false"
        src="@/assets/img/images/icon/close-icon.png"
      />
    </div>

    <div class="asset-detail-card">
      <div class="total-asset">
        <span class="total-label">{{
          $t("asset.estimated_total_assets")
        }}</span>
        <span class="total-amount">{{ totalBalance }}</span>
        <span class="total-value"
          >≈ {{ currencySymbol }} {{ formatCurrency(assetInfo) }}</span
        >
      </div>

      <div class="asset-stats">
        <div class="stat-item">
          <span class="stat-label">{{ $t("asset.available") }}</span>
          <span class="stat-value">{{ formatBalance(assetInfo) }}</span>
        </div>
        <div class="stat-divider"></div>
        <div class="stat-item">
          <span class="stat-label">{{ $t("asset.frozen") }}</span>
          <span class="stat-value">{{ formatFrozen(assetInfo) }}</span>
        </div>
      </div>
    </div>

    <div class="market-section">
      <span class="section-title">{{ $t("asset.market_trade") }}</span>
      <div class="market-list" v-if="marketList.length > 0">
        <div
          class="market-item"
          v-for="(item, index) in marketList"
          :key="index"
          @click="goTrade(item)"
        >
          <span class="market-symbol">{{
            formatMarketSymbol(item.symbol)
          }}</span>
          <div class="market-info">
            <span class="market-price">{{ formatPrice(item) }}</span>
            <span :class="['market-change', item.change >= 0 ? 'up' : 'down']">
              {{ item.change >= 0 ? "+" : ""
              }}{{ (item.change * 100).toFixed(2) }}%
            </span>
          </div>
        </div>
      </div>
      <div v-else class="no-market">
        <span class="no-market-text">{{ $t("asset.no_market_data") }}</span>
      </div>
    </div>

    <div class="action-bar">
      <div class="action-btn primary" :class="{ disabled: !canRecharge }" @click="goRecharge">
        <span class="btn-text">{{ $t("asset.recharge") }}</span>
      </div>
      <div class="action-btn secondary" @click="goWithdraw">
        <span class="btn-text">{{ $t("asset.withdraw") }}</span>
      </div>
      <div class="action-btn secondary" @click="goRecords">
        <span class="btn-text">{{ $t("asset.records") }}</span>
      </div>
    </div>
  </el-dialog>
</template>

<script>
import { mapState } from "vuex";
import precisionMixin from "@/mixins/precisionMixin";
export default {
  name: "AssetDetailDialog",
  mixins: [precisionMixin],
  props: {
    asset: {
      type: Object,
      required: true,
    },
    visible: {
      type: Boolean,
      default: false,
    },
  },
  data() {
    return {
      dialogVisible: this.visible,
      assetInfo: {},
      marketList: [],
      canRecharge: false,
    };
  },
  watch: {
    visible(val) {
      this.dialogVisible = val;
      if (val) {
        this.init();
      }
    },
    dialogVisible(val) {
      this.$emit("update:visible", val);
    },
  },
  computed: {
    ...mapState(["user", "errorCode"]),
    displayCurrency() {
      const type = (this.assetInfo.assetType || "").toUpperCase();
      return type === "USDT" ? "USD" : type;
    },
    currencySymbol() {
      const currency = (this.assetInfo.currency || "").toUpperCase();
      if (currency === "HKD") {
        return "HKD";
      } else if (currency === "USDT" || currency === "USD") {
        return "USD";
      } else {
        return currency + " ";
      }
    },
    totalBalance() {
      const balance =
        Number(this.assetInfo.balanceAvailable || 0) +
        Number(this.assetInfo.balanceFrozen || 0);
      const symbol = `${this.assetInfo.assetType}/${
        this.assetInfo.currency || "USDT"
      }`;
      const precision = this.getSymbolPrecision(symbol, "coin");
      return balance.toFixed(precision);
    },
  },
  methods: {
    init() {
      this.assetInfo = { ...this.asset };
      this.getMarketData();
      this.checkRecharge();
    },
    checkRecharge() {
      if (!this.assetInfo.assetType) {
        this.canRecharge = false;
        return;
      }
      this.canRecharge = false;
      this.$axios
        .post("/Coin/getSupportedNetworks", {
          coinType: this.assetInfo.assetType,
          network: "",
        })
        .then((res) => {
          if (res.data.code === this.errorCode.ok && res.data.networks && res.data.networks.length > 0) {
            const network = res.data.networks[0];
            this.$axios
              .post("/userAssets/deposit", {
                coinType: this.assetInfo.assetType,
                network: network,
              })
              .then((res) => {
                if (res.data.code === this.errorCode.ok) {
                  this.canRecharge = true;
                } else if (res.data.code === "DEPOSIT000") {
                  this.canRecharge = false;
                } else {
                  this.canRecharge = false;
                }
              })
              .catch(() => {
                this.canRecharge = false;
              });
          } else {
            this.canRecharge = false;
          }
        })
        .catch(() => {
          this.canRecharge = false;
        });
    },
    getMarketData() {
      this.$axios
        .post("/exchangeCoin/getAll", {})
        .then((res) => {
          if (
            res &&
            res.data &&
            res.data.code === this.errorCode.ok &&
            res.data.data
          ) {
            const ticker = res.data.data.ticker || {};
            this.marketList = Object.keys(ticker)
              .filter(
                (key) =>
                  typeof key === "string" &&
                  (key.startsWith("RWA/HKD") ||
                    key.startsWith("RWA/USDT") ||
                    key.startsWith(this.assetInfo.assetType)),
              )
              .map((key) => {
                const data = ticker[key] || {};
                return {
                  symbol: data.s || key,
                  price: data.c || 0,
                  change: data.p || 0,
                };
              });
          }
        })
        .catch(() => {
          this.marketList = [];
        });
    },
    formatBalance(item) {
      const symbol = `${item.assetType}/${item.currency || "USDT"}`;
      const precision = this.getSymbolPrecision(symbol, "coin");
      return Number(item.balanceAvailable || 0).toFixed(precision);
    },
    formatFrozen(item) {
      const symbol = `${item.assetType}/${item.currency || "USDT"}`;
      const precision = this.getSymbolPrecision(symbol, "coin");
      return Number(item.balanceFrozen || 0).toFixed(precision);
    },
    formatCurrency(item) {
      return Number(item.assetCurrency || 0).toFixed(2);
    },
    formatPrice(item) {
      const precision = this.getSymbolPrecision(item.symbol, "price");
      return Number(item.price || 0).toFixed(precision);
    },
    formatMarketSymbol(symbol) {
      if (!symbol) return "";
      const upper = symbol.toUpperCase();
      if (upper === "USDT") return "USD";
      return upper.replace("/USDT", "/USD");
    },
    goTrade(item) {
      if (window.rwa) {
        window.rwa.gotoKline(item.symbol);
      } else {
        this.$router.push({
          path: "/ctc",
          query: { symbol: item.symbol },
        });
      }
      this.dialogVisible = false;
    },
    goRecharge() {
      if (!this.canRecharge) {
        this.$message.warning(this.$t("asset.can_not_recharge"));
        return;
      }
      if (this.assetInfo.canRecharge === -1) {
        this.$message.warning(this.$t("asset.can_not_recharge"));
        return;
      }
      this.$emit("recharge", this.assetInfo);
      this.dialogVisible = false;
    },
    goWithdraw() {
      if (this.assetInfo.canWithdraw === -1) {
        this.$message.warning(this.$t("asset.can_not_withdraw"));
        return;
      }
      if (!this.judgeValidateNum()) {
        return;
      }
      if (this.user.statusKycLevel !== 1) {
        this.$message.warning(this.$t("tipsVal.userAuthenticate"));
        return;
      }
      if (this.user.tranPassword === -1) {
        this.$confirm(
          this.$t("partner.need_set_password"),
          this.$t("user_center.tip"),
          {
            confirmButtonText: this.$t("partner.set_now"),
            cancelButtonText: this.$t("partner.cancel"),
            type: "warning",
          },
        )
          .then(() => {
            this.$router.push({ path: "/user" });
          })
          .catch(() => {});
      } else {
        this.$emit("withdraw", this.assetInfo);
        this.dialogVisible = false;
      }
    },
    judgeValidateNum() {
      let sum = 0;
      if (this.user.phoneSecurity === 1) sum++;
      if (this.user.emailSecurity === 1) sum++;
      if (this.user.googleSecurity === 1) sum++;
      if (sum >= 2) {
        return true;
      } else {
        this.$message.warning(this.$t("tipsVal.validate_warning"));
        return false;
      }
    },
    goRecords() {
      this.$emit("records", this.assetInfo);
      this.dialogVisible = false;
    },
  },
};
</script>

<style lang="less" scoped>
.asset-detail-dialog {
  /deep/ .el-dialog {
    background: #ffffff;
    border-radius: 10px;
  }
  /deep/ .el-dialog__body {
    padding: 0;
  }
}
/deep/ .el-dialog__header {
  padding: 0 !important;
  display: none;
}
.dialog-header {
  display: flex;
  justify-content: space-between;
  align-items: center;
  padding: 24px 32px;
  border-bottom: 1px solid #e2e3e3;

  .header-left {
    display: flex;
    align-items: center;
    gap: 16px;

    .asset-icon {
      width: 48px;
      height: 48px;
      background: #f5f6f7;
      display: flex;
      align-items: center;
      justify-content: center;
      border-radius: 12px;
      overflow: hidden;

      .icon-image {
        width: 40px;
        height: 40px;
        object-fit: cover;
      }

      .icon-text {
        font-family: PingFang SC, PingFang SC;
        font-weight: 700;
        font-size: 20px;
        color: #333333;
      }
    }

    .asset-title {
      font-family: PingFang SC, PingFang SC;
      font-weight: 800;
      font-size: 24px;
      color: #333333;
    }
  }

  .close-img {
    width: 14px;
    height: 14px;
    cursor: pointer;
  }
}

.asset-detail-card {
  padding: 24px;
  background: #fafbfc;

  .total-asset {
    margin-bottom: 32px;

    .total-label {
      font-family: PingFang SC, PingFang SC;
      font-weight: 400;
      font-size: 14px;
      color: #999999;
      display: block;
      margin-bottom: 12px;
    }

    .total-amount {
      font-family: PingFang SC, PingFang SC;
      font-weight: 800;
      font-size: 32px;
      color: #333333;
      display: block;
      margin-bottom: 8px;
      line-height: 1.2;
    }

    .total-value {
      font-family: PingFang SC, PingFang SC;
      font-weight: 400;
      font-size: 14px;
      color: #999999;
    }
  }

  .asset-stats {
    display: flex;
    justify-content: space-between;
    align-items: center;
    padding-top: 24px;
    border-top: 1px solid #e2e3e3;

    .stat-item {
      flex: 1;
      text-align: center;

      .stat-label {
        font-family: PingFang SC, PingFang SC;
        font-weight: 400;
        font-size: 14px;
        color: #999999;
        display: block;
        margin-bottom: 8px;
      }

      .stat-value {
        font-family: PingFang SC, PingFang SC;
        font-weight: 600;
        font-size: 20px;
        color: #333333;
      }
    }

    .stat-divider {
      width: 1px;
      height: 40px;
      background: #e2e3e3;
    }
  }
}

.market-section {
  padding: 24px;

  .section-title {
    font-family: PingFang SC, PingFang SC;
    font-weight: 600;
    font-size: 18px;
    color: #333333;
    display: block;
    margin-bottom: 20px;
  }

  .market-list {
    display: flex;
    flex-wrap: wrap;
    gap: 16px;

    .market-item {
      width: calc((100% - 32px) / 3);
      background: #f5f6f7;
      border-radius: 9px;
      padding: 16px;
      cursor: pointer;
      transition: all 0.2s;

      &:hover {
        background: var(--box-bg-color);
      }

      .market-symbol {
        font-family: PingFang SC, PingFang SC;
        font-weight: 400;
        font-size: 14px;
        color: #666666;
        display: block;
        margin-bottom: 8px;
      }

      .market-info {
        display: flex;
        justify-content: space-between;
        align-items: center;

        .market-price {
          font-family: PingFang SC, PingFang SC;
          font-weight: 600;
          font-size: 16px;
          color: #333333;
        }

        .market-change {
          font-family: PingFang SC, PingFang SC;
          font-weight: 400;
          font-size: 14px;
        }

        .market-change.up {
          color: #42cb83;
        }

        .market-change.down {
          color: #f04c66;
        }
      }
    }
  }

  .no-market {
    text-align: center;
    padding: 40px;

    .no-market-text {
      font-family: PingFang SC, PingFang SC;
      font-weight: 400;
      font-size: 14px;
      color: #999999;
    }
  }
}

.action-bar {
  display: flex;
  gap: 12px;
  padding: 24px 32px;
  padding-bottom: 32px;
  border-top: 1px solid #e2e3e3;

  .action-btn {
    flex: 1;
    height: 48px;
    border-radius: 9px;
    display: flex;
    align-items: center;
    justify-content: center;
    cursor: pointer;
    transition: all 0.2s;

    &.primary {
      background: var(--primary-color);

      .btn-text {
        color: #ffffff;
      }
    }

    &.secondary {
      background: #f5f6f7;

      .btn-text {
        color: #666666;
      }
    }

    &.disabled {
      background: #d6d8db !important;
      cursor: not-allowed;
      opacity: 0.7;

      .btn-text {
        color: #ffffff !important;
      }
    }

    &:hover {
      opacity: 0.9;
    }

    &.disabled:hover {
      opacity: 0.7;
    }

    .btn-text {
      font-family: PingFang SC, PingFang SC;
      font-weight: 600;
      font-size: 14px;
    }
  }
}
</style>
